import React, { useState, useEffect, useCallback, useRef } from "react";

import { listProjects, createProject, updateProject, deleteProject, listProjectPages, getScreen, saveScreen } from "../../../api";
import { parseProjectExport } from "../../../utils/projectImport";
import { useNotify } from "../../../ToastContext";
import { usePermission } from "../../../UserContext";
import searchIconSrc from "../../../assets/search.svg";
import plusIcon from "../../../assets/plus.svg";
import xmarkIcon from "../../../assets/xmark.svg";
import moreIcon from "../../../assets/more-horiz.svg";

const ProjectsPage = () => {
  const canConfigure = usePermission("configure");
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState("");
  const [creating, setCreating] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [editName, setEditName] = useState("");
  const [editTitle, setEditTitle] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [editSaving, setEditSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [menuOpen, setMenuOpen] = useState(null);
  const [menuStyle, setMenuStyle] = useState({});
  const [importing, setImporting] = useState(false);
  const menuRef = useRef(null);
  const importRef = useRef(null);
  const notify = useNotify();

  const load = useCallback(async () => {
    try {
      const data = await listProjects();
      setProjects(data.projects || []);
    } catch (err) {
      notify({ type: "error", title: "Error", message: err.message || "Failed to load projects" });
    } finally {
      setLoading(false);
    }
  }, [notify]);

  useEffect(() => { load(); }, [load]);

  // Refresh when the AI deletes a project.
  useEffect(() => {
    const handler = (e) => {
      if (e.detail?.resource === "projects") load();
    };
    window.addEventListener("gw-ai-resource-changed", handler);
    return () => window.removeEventListener("gw-ai-resource-changed", handler);
  }, [load]);

  // AI propose-project: open the New Project modal pre-filled with name.
  useEffect(() => {
    const handler = (e) => {
      const payload = e.detail?.payload;
      if (!payload || typeof payload !== "object") return;
      if (typeof payload.name === "string") setNewName(payload.name);
      setShowCreate(true);
    };
    window.addEventListener("gw-ai-propose-project-page", handler);
    return () => window.removeEventListener("gw-ai-propose-project-page", handler);
  }, []);

  const handleCreate = async () => {
    if (!newName.trim()) return;
    setCreating(true);
    try {
      await createProject({ name: newName.trim() });
      notify({ type: "success", title: "Created", message: `Project "${newName.trim()}" created.` });
      setNewName("");
      setShowCreate(false);
      await load();
    } catch (err) {
      notify({ type: "error", title: "Error", message: err.message || "Failed to create project" });
    } finally {
      setCreating(false);
    }
  };

  const openEdit = (p) => {
    setEditTarget(p);
    setEditName(p.name);
    setEditTitle(p.settings?.title || "");
    setEditDescription(p.settings?.description || "");
    setDeleteTarget(null);
  };

  const handleSaveEdit = async () => {
    if (!editTarget || !editName.trim()) return;
    setEditSaving(true);
    try {
      await updateProject(editTarget.id, {
        name: editName.trim(),
        settings: {
          ...(editTarget.settings || {}),
          title: editTitle.trim(),
          description: editDescription.trim(),
        },
      });
      notify({ type: "success", title: "Updated", message: `Project settings saved.` });
      setEditTarget(null);
      await load();
    } catch (err) {
      notify({ type: "error", title: "Error", message: err.message || "Failed to update project" });
    } finally {
      setEditSaving(false);
    }
  };

  const editDirty = editTarget && (
    editName.trim() !== editTarget.name ||
    editTitle.trim() !== (editTarget.settings?.title || "") ||
    editDescription.trim() !== (editTarget.settings?.description || "")
  );

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteProject(deleteTarget.id);
      notify({ type: "success", title: "Deleted", message: `Project "${deleteTarget.name}" and its screens deleted.` });
      setDeleteTarget(null);
      setEditTarget(null);
      await load();
    } catch (err) {
      notify({ type: "error", title: "Error", message: err.message || "Failed to delete project" });
    } finally {
      setDeleting(false);
    }
  };

  const openMenu = (e, projectId) => {
    e.stopPropagation();
    const r = e.currentTarget.getBoundingClientRect();
    setMenuStyle({
      position: "fixed",
      top: `${Math.round(r.bottom + 4)}px`,
      right: `${Math.max(12, Math.round(window.innerWidth - r.right))}px`,
    });
    setMenuOpen(menuOpen === projectId ? null : projectId);
  };

  // Close menu on outside click
  useEffect(() => {
    if (!menuOpen) return;
    const onDoc = (e) => {
      if (menuRef.current?.contains(e.target)) return;
      setMenuOpen(null);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [menuOpen]);

  const handleDuplicate = async (p) => {
    setMenuOpen(null);
    try {
      // Create the new project
      const projectRes = await createProject({ name: `${p.name} (Copy)` });
      const newProjectId = projectRes?.project?.id;
      if (!newProjectId) {
        notify({ type: "error", title: "Error", message: "Project created but no ID returned." });
        return;
      }

      // Fetch all pages from the original project
      const pagesData = await listProjectPages(p.id);
      const pages = pagesData.pages || [];

      let copied = 0;
      for (const page of pages) {
        try {
          const screen = await getScreen(page.screenId);
          const s = screen.screen || screen;
          await saveScreen({
            name: s.name || "Untitled",
            slug: s.slug || "",
            projectId: newProjectId,
            html: s.html || "",
            css: s.css || "",
            js: s.js || "",
            scripts: s.scripts || {},
            projectData: s.projectData || null,
            meta: s.meta || {},
          });
          copied++;
        } catch {}
      }

      await load();
      notify({ type: "success", title: "Duplicated", message: `"${p.name}" duplicated with ${copied} page${copied !== 1 ? "s" : ""}.` });
    } catch (err) {
      notify({ type: "error", title: "Error", message: err.message || "Failed to duplicate" });
    }
  };

  const handleExport = async (p) => {
    setMenuOpen(null);
    try {
      const pagesData = await listProjectPages(p.id);
      const pages = pagesData.pages || [];
      // Fetch full screen data for each page
      const screens = await Promise.all(
        pages.map(async (page) => {
          try {
            return await getScreen(page.screenId);
          } catch {
            return { screenId: page.screenId, name: page.name, error: "Failed to fetch" };
          }
        })
      );
      const exportData = {
        project: p,
        pages: screens,
        exportedAt: new Date().toISOString(),
        version: "1.0",
      };
      const data = JSON.stringify(exportData, null, 2);
      const blob = new Blob([data], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${p.id || p.name}-export.json`;
      a.click();
      URL.revokeObjectURL(url);
      notify({ type: "success", title: "Exported", message: `"${p.name}" exported with ${screens.length} page${screens.length !== 1 ? "s" : ""}.` });
    } catch (err) {
      notify({ type: "error", title: "Export Failed", message: err.message || "Failed to export project" });
    }
  };

  const handleToggleDisable = async (p) => {
    setMenuOpen(null);
    const isDisabled = p.settings?.disabled === true;
    try {
      await updateProject(p.id, {
        settings: { ...(p.settings || {}), disabled: !isDisabled },
      });
      notify({
        type: "success",
        title: isDisabled ? "Enabled" : "Disabled",
        message: isDisabled
          ? `"${p.name}" is now accessible at runtime.`
          : `"${p.name}" runtime access is now blocked.`,
      });
      await load();
    } catch (err) {
      notify({ type: "error", title: "Error", message: err.message || "Failed to update project" });
    }
  };

  const handleMenuDelete = (p) => {
    setMenuOpen(null);
    setEditTarget(p);
    setEditName(p.name);
    setDeleteTarget(p);
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (importRef.current) importRef.current.value = "";
    if (!file) return;

    setImporting(true);
    try {
      const text = await file.text();

      // Parse and validate
      let parsed;
      try {
        parsed = parseProjectExport(text);
      } catch (err) {
        notify({ type: "error", title: "Invalid File", message: err.message });
        setImporting(false);
        return;
      }

      // Create the project
      const projectRes = await createProject({ name: parsed.project.name });
      const newProjectId = projectRes?.project?.id;
      if (!newProjectId) {
        notify({ type: "error", title: "Import Failed", message: "Project created but no ID returned." });
        setImporting(false);
        return;
      }

      // Import each page
      let imported = 0;
      let failed = 0;
      for (const page of parsed.pages) {
        try {
          await saveScreen({ ...page, projectId: newProjectId });
          imported++;
        } catch {
          failed++;
        }
      }

      await load();
      const msg = failed > 0
        ? `"${parsed.project.name}" imported with ${imported} page${imported !== 1 ? "s" : ""} (${failed} failed).`
        : `"${parsed.project.name}" imported with ${imported} page${imported !== 1 ? "s" : ""}.`;
      notify({ type: "success", title: "Imported", message: msg });
    } catch (err) {
      notify({ type: "error", title: "Import Failed", message: err.message || "Unexpected error." });
    } finally {
      setImporting(false);
    }
  };

  const filtered = projects.filter((p) => {
    if (!searchQuery) return true;
    return p.name?.toLowerCase().includes(searchQuery.toLowerCase())
      || p.id?.toLowerCase().includes(searchQuery.toLowerCase());
  });

  const formatDate = (d) => {
    if (!d) return "";
    const date = new Date(d);
    const now = new Date();
    const diff = now - date;
    if (diff < 60000) return "just now";
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    return date.toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="gwContentGrid">
        <section className="gwFormStack">
          <div className="gwPageHeader">
            <div>
              <div className="gwPageTitle">Projects</div>
              <div className="gwPageSubtitle">Loading...</div>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="gwContentGrid">
      <section className="gwFormStack">
        <div className="gwPageHeader">
          <div>
            <div className="gwPageTitle">Projects</div>
            <div className="gwPageSubtitle">
              {projects.length} project{projects.length !== 1 ? "s" : ""}
            </div>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button
              className="gwSecondaryBtn"
              type="button"
              onClick={() => importRef.current?.click()}
              disabled={importing || !canConfigure}
              title={canConfigure ? undefined : "Requires configure permission"}
              style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
            >
              {importing ? "Importing..." : "Import"}
            </button>
            <input
              ref={importRef}
              type="file"
              accept=".json"
              style={{ display: "none" }}
              onChange={handleImport}
            />
            <button
              className="gwPrimaryBtn"
              type="button"
              onClick={() => { setShowCreate(true); setNewName(""); }}
              disabled={!canConfigure}
              title={canConfigure ? undefined : "Requires configure permission"}
              style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
            >
              <img src={plusIcon} alt="" className="gwIcon" style={{ width: 14, height: 14 }} />
              <span>New Project</span>
            </button>
          </div>
        </div>

        {/* Create Modal */}
        {showCreate && (
          <div className="gwOverlay" onClick={() => setShowCreate(false)}>
            <div className="gwModal" onClick={(e) => e.stopPropagation()} style={{ width: 420 }}>
              <div className="gwModalHeader">
                <div>
                  <div className="gwModalTitle">New Project</div>
                  <div className="gwModalSubtitle">Create a new project</div>
                </div>
                <button className="gwModalClose" type="button" onClick={() => setShowCreate(false)}>
                  <img src={xmarkIcon} alt="Close" className="gwIcon" style={{ width: 10, height: 10, opacity: 0.7 }} />
                </button>
              </div>
              <div className="gwModalBody">
                <div className="gwField">
                  <label className="gwLabel">Project Name</label>
                  <input
                    className="gwInput"
                    placeholder="e.g. Plant Floor HMI"
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleCreate()}
                    autoFocus
                  />
                </div>
              </div>
              <div className="gwModalFooter" style={{ justifyContent: "space-between" }}>
                <div style={{ display: "flex", gap: 8 }}>
                  <button
                    className="gwPrimaryBtn"
                    type="button"
                    onClick={handleCreate}
                    disabled={creating || !newName.trim()}
                  >
                    {creating ? "Creating..." : "Create Project"}
                  </button>
                  <button className="gwGhostBtn" type="button" onClick={() => setShowCreate(false)}>Cancel</button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Project list */}
        <div className="gwCardSection">
          <div className="gwSectionTitle" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span>Projects</span>
            <div className="gwSearch" style={{ width: 200, height: 30 }}>
              <img src={searchIconSrc} alt="" className="gwSearchIcon" />
              <input
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{ fontSize: 12 }}
              />
            </div>
          </div>
          <div className="gwCardSectionBody" style={{ padding: 0 }}>
            {filtered.map((p) => (
              <div key={p.id} className="umUserRow"
                data-gw-resource="projects" data-gw-id={p.id} data-gw-name={p.name}
                style={{ cursor: "pointer" }} onClick={() => openEdit(p)}>
                <div style={{
                  width: 36, height: 36, borderRadius: 10, flexShrink: 0,
                  background: "rgba(80,140,255,0.12)", border: "1px solid rgba(80,140,255,0.2)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 14, fontWeight: 700, color: "rgba(120,170,255,0.9)",
                }}>
                  {(p.name || "?")[0].toUpperCase()}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span className="umUserName" style={p.settings?.disabled ? { opacity: 0.5 } : undefined}>{p.name}</span>
                    {p.settings?.disabled && (
                      <span style={{
                        fontSize: 9, textTransform: "uppercase", letterSpacing: 0.5, padding: "2px 6px",
                        borderRadius: 4, background: "var(--gw-danger-bg)", color: "var(--gw-danger)",
                        border: "1px solid var(--gw-danger-border)", fontWeight: 600,
                      }}>
                        Disabled
                      </span>
                    )}
                    <span style={{ fontSize: 11, color: "var(--gw-muted)", fontFamily: "monospace" }}>{p.id}</span>
                  </div>
                  <div className="umUserMeta">
                    {p.updatedAt ? `Updated ${formatDate(p.updatedAt)}` : `Created ${formatDate(p.createdAt)}`}
                  </div>
                </div>
                <button
                  type="button"
                  className="gwIconButton"
                  style={{ flexShrink: 0 }}
                  onClick={(e) => openMenu(e, p.id)}
                >
                  <img src={moreIcon} alt="More" className="gwIcon" style={{ width: 16, height: 16 }} />
                </button>
                {menuOpen === p.id && (
                  <div ref={menuRef} className="gwProjectMenu" style={menuStyle} onClick={(e) => e.stopPropagation()}>
                    <button className="gwProjectMenuItem" type="button"
                      disabled={!canConfigure}
                      title={canConfigure ? undefined : "Requires configure permission"}
                      onClick={() => { setMenuOpen(null); openEdit(p); }}>
                      Edit
                    </button>
                    <button className="gwProjectMenuItem" type="button"
                      disabled={!canConfigure}
                      title={canConfigure ? undefined : "Requires configure permission"}
                      onClick={() => handleDuplicate(p)}>
                      Duplicate
                    </button>
                    <button className="gwProjectMenuItem" type="button"
                      onClick={() => handleExport(p)}>
                      Export
                    </button>
                    <button className="gwProjectMenuItem" type="button"
                      disabled={!canConfigure}
                      title={canConfigure ? undefined : "Requires configure permission"}
                      onClick={() => handleToggleDisable(p)}>
                      {p.settings?.disabled ? "Enable" : "Disable"}
                    </button>
                    <div className="gwProfileDivider" />
                    <button className="gwProjectMenuItem gwProjectMenuDanger" type="button"
                      disabled={!canConfigure}
                      title={canConfigure ? undefined : "Requires configure permission"}
                      onClick={() => handleMenuDelete(p)}>
                      Delete
                    </button>
                  </div>
                )}
              </div>
            ))}
            {filtered.length === 0 && (
              <div style={{ padding: 24, textAlign: "center", color: "var(--gw-muted)", fontSize: 13 }}>
                {searchQuery ? "No projects match your search." : "No projects yet. Create one above."}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Edit modal */}
      {editTarget && (
        <div className="gwOverlay" onClick={() => setEditTarget(null)}>
          <div className="gwModal" onClick={(e) => e.stopPropagation()} style={{ width: 420 }}>
            <div className="gwModalHeader">
              <div>
                <div className="gwModalTitle">{editTarget.name}</div>
                <div className="gwModalSubtitle">
                  ID: {editTarget.id}
                  {editTarget.createdAt && ` \u00b7 Created ${new Date(editTarget.createdAt).toLocaleDateString()}`}
                </div>
              </div>
              <button className="gwModalClose" type="button" onClick={() => setEditTarget(null)}>
                <img src={xmarkIcon} alt="Close" className="gwIcon" style={{ width: 10, height: 10, opacity: 0.7 }} />
              </button>
            </div>

            <div className="gwModalBody">
              <div className="gwField">
                <label className="gwLabel">Name</label>
                <input
                  className="gwInput"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                />
                <span className="gwHint">The internal project identifier. Used for URLs and the file system.</span>
              </div>
              <div className="gwField">
                <label className="gwLabel">Title</label>
                <input
                  className="gwInput"
                  placeholder={editName || "Project title"}
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                />
                <span className="gwHint">Display title shown to users. If empty, the name is used.</span>
              </div>
              <div className="gwField">
                <label className="gwLabel">Description</label>
                <textarea
                  className="gwTextarea"
                  placeholder="What is this project for?"
                  value={editDescription}
                  onChange={(e) => setEditDescription(e.target.value)}
                  rows={3}
                />
              </div>
            </div>

            <div className="gwModalFooter" style={{ justifyContent: "space-between" }}>
              <div style={{ display: "flex", gap: 8 }}>
                <button className="gwPrimaryBtn" type="button" onClick={handleSaveEdit}
                  disabled={editSaving || !editDirty}>
                  {editSaving ? "Saving..." : "Save"}
                </button>
                <button className="gwGhostBtn" type="button" onClick={() => setEditTarget(null)}>
                  Cancel
                </button>
              </div>
              {!deleteTarget ? (
                <button className="gwDangerBtn" type="button" onClick={() => setDeleteTarget(editTarget)}
                  style={{ fontSize: 12 }}>
                  Delete
                </button>
              ) : (
                <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                  <button className="gwDangerBtn" type="button" onClick={handleDelete}
                    disabled={deleting}>{deleting ? "..." : "Confirm"}</button>
                  <button className="gwGhostBtn" type="button" onClick={() => setDeleteTarget(null)}
                    style={{ fontSize: 12 }}>Cancel</button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export { ProjectsPage };
